import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/utils/formatPrice";
import type { PlayerPriceResult } from "@/lib/playerPricing";
import { useTranslation } from "react-i18next";

interface PlayerProfile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  level: number | null;
  categorySlug?: string;
  categoryName?: string;
}

interface Step2OpenProps {
  startTime: Date;
  durationMinutes: number;
  onDurationChange: (minutes: number) => void;
  durationSteps: number[];
  isMultiSlotMode: boolean;
  userId: string;
  organizationId: string;
  invitedPlayers: PlayerProfile[];
  onInvitedPlayersChange: (players: PlayerProfile[]) => void;
  totalPrice: number;
  totalMaxPlayers: number;
  playerPrices: PlayerPriceResult[];
  pricesReady: boolean;
  totalCourts: number;
}

export default function Step2Open({
  startTime,
  durationMinutes,
  onDurationChange,
  durationSteps,
  isMultiSlotMode,
  userId,
  organizationId,
  invitedPlayers,
  onInvitedPlayersChange,
  totalPrice: _totalPrice,
  totalMaxPlayers,
  playerPrices,
  pricesReady,
  totalCourts,
}: Step2OpenProps) {
  const { t } = useTranslation("booking");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<PlayerProfile[]>([]);
  const [_searching, setSearching] = useState(false);

  // Use async category-aware price when ready, otherwise show loading
  const creatorPrice = pricesReady && playerPrices.length > 0 ? playerPrices[0] : null;
  const perPersonPrice = creatorPrice?.courtShare ?? null;
  const confirmedPlayers = playerPrices.length;
  const openSpots = Math.max(0, totalMaxPlayers - confirmedPlayers);

  // Player search
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      // View enforces profile_visibility at row level — drop the .neq filter
      const { data } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, avatar_url, level")
        .ilike("full_name", `%${searchTerm}%`)
        .neq("id", userId)
        .limit(8);

      if (data && data.length > 0) {
        const { data: customers } = await supabase
          .from("customers")
          .select("user_id, customer_categories(slug, name)")
          .eq("organization_id", organizationId)
          .in("user_id", data.map((d) => d.id));

        const catMap = new Map(
          (customers ?? []).map((c: { user_id: string; customer_categories: { slug: string; name: string } | null }) => [
            c.user_id,
            c.customer_categories,
          ])
        );

        setSearchResults(
          (data as PlayerProfile[]).map((p) => ({
            ...p,
            categorySlug: catMap.get(p.id)?.slug ?? "standard",
            categoryName: catMap.get(p.id)?.name ?? "Standard",
          }))
        );
      } else {
        setSearchResults([]);
      }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, userId, organizationId]);

  const addPlayer = (p: PlayerProfile) => {
    if (!invitedPlayers.find((ip) => ip.id === p.id)) {
      onInvitedPlayersChange([...invitedPlayers, p]);
    }
    setSearchTerm("");
    setSearchResults([]);
  };

  const removePlayer = (id: string) => {
    onInvitedPlayersChange(invitedPlayers.filter((p) => p.id !== id));
  };

  return (
    <div className="space-y-5 py-4">
      {/* Duration selector -- hidden in multi-slot mode */}
      {!isMultiSlotMode && durationSteps.length > 1 && (
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">
            {t("playTime")}
          </Label>
          <div className="flex flex-wrap gap-2">
            {durationSteps.map((minutes) => (
              <button
                key={minutes}
                type="button"
                onClick={() => onDurationChange(minutes)}
                className={cn(
                  "px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors min-h-[44px]",
                  durationMinutes === minutes
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-muted hover:border-muted-foreground/30"
                )}
              >
                {minutes} min
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Spots display */}
      <div className="flex items-center justify-between rounded-lg bg-muted px-3 py-2.5">
        <Label className="text-sm">{t("numberOfSpots")}</Label>
        <span className="text-sm font-medium">
          {totalMaxPlayers} {t("players")} ({totalCourts}{" "}
          {totalCourts === 1 ? t("court") : t("courts")})
        </span>
      </div>

      <div className="border-t pt-4" />

      {/* Player invite (optional) */}
      <div>
        <Label className="text-xs text-muted-foreground mb-1.5 block">
          {t("invitePlayersOptional")}
        </Label>
        {invitedPlayers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {invitedPlayers.map((p) => (
              <Badge key={p.id} variant="secondary" className="gap-1 pr-1">
                {p.full_name ?? t("unknown")}
                <button
                  onClick={() => removePlayer(p.id)}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("searchByName")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 min-h-[44px]"
          />
        </div>
        {searchResults.length > 0 && (
          <div className="border rounded-md mt-1 max-h-32 overflow-y-auto divide-y">
            {searchResults.map((p) => (
              <button
                key={p.id}
                onClick={() => addPlayer(p)}
                className="w-full flex items-center gap-2 p-2 text-sm hover:bg-muted/50 text-left min-h-[44px]"
              >
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-[10px]">
                    {(p.full_name ?? "?")[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 truncate">
                  {p.full_name ?? t("unknown")}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t pt-4" />

      {/* Price info — T1/G-001: the share is paid at JOIN (decision 9), not at
          create. Addons removed: they were charged with the (now removed)
          create-time booking payment and would have been silent dead UI. */}
      <div className="rounded-lg border p-3 space-y-2">
        <p className="text-sm font-medium">{t("yourCost")}</p>
        <div className="flex justify-between items-center">
          <span className="text-sm">{t("youPay")}</span>
          {perPersonPrice != null ? (
            <span className="text-lg font-bold">{formatPrice(perPersonPrice)}</span>
          ) : (
            <span className="inline-block w-16 h-6 bg-muted animate-pulse rounded" />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("yourSpot", { max: totalMaxPlayers, courts: totalCourts, courtLabel: totalCourts === 1 ? t("court") : t("courts") })}
        </p>
        {openSpots > 0 && perPersonPrice != null && (
          <p className="text-xs text-muted-foreground">
            {t("openSpotsInfo", { count: openSpots, price: formatPrice(perPersonPrice) })}
          </p>
        )}
        <p className="text-xs text-muted-foreground">{t("payAtJoinInfo")}</p>
      </div>
    </div>
  );
}
